import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import "./StandardCatalogItemLookup.scss";
import * as _ from "lodash-es";
import { useParams } from "react-router-dom";
import ICreateOrderEntryItemsDTO from "../../../models/DTO/ICreateOrderEntryItemsDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IKitDetails from "../../../models/IKitDetails";
import INewOrder_Item from "../../../models/INewOrder_Item";
import CatalogItemService from "../../../services/CatalogItemService";
import OrderEntryService from "../../../services/OrderEntryService";
import UIService from "../../../services/UIService";
import ReactTooltip from "react-tooltip";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { PackQtyViewer } from "../../shared/PackQtyViewer/PackQtyViewer";
import Typeahead from "../../shared/Typeahead/Typeahead";
import OwButton from "../../shared/OwButton/OwButton";
import CatalogViewerSidebar from "../CatalogViewerSidebar/CatalogViewerSidebar";
import OwLabel from "../../shared/OwLabel/OwLabel";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IInventoryMessage from "../../../models/IInventoryMessage";
import IOrder from "../../../models/IOrder";
import { KitOptionSelector } from "../KitOptionSelector/KitOptionSelector";
import OwCatalogCategoryViewer from "../OwCatalogCategoryViewer/OwCatalogCategoryViewer";
import IdbService from "../../../services/IdbService";
import ProductGroupComponentEnum from "../../../models/ProductGroupComponentEnum";
import { CatalogItemLookupDetails } from "./CatalogItemLookupDetails/CatalogItemLookupDetails";
import IOnGetWarehousesDTO from "../../../models/DTO/IOnGetWarehousesDTO";
import IWarehouse from "../../../models/IWarehouse";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import IDiscount from "../../../models/IDiscount";
import { CatalogItemDescription } from "../../shared/CatalogItemDescription/CatalogItemDescription";
import { UomSetSelectOption } from "../../shared/UomSetSelectOption/UomSetSelectOption";
import IUnit_Of_Measurement_Set from "../../../models/IUnit_Of_Measurement_Set";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import { AccountRecentItemsSidebar } from "../AccountRecentItemsSidebar/AccountRecentItemsSidebar";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import { ItemLookupButtonSet } from "./ItemLookupButtonSet/ItemLookupButtonSet";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import CreateCatalogItemSidebar from "../../catalogs/CatalogOverview/CreateCatalogItemSidebar/CreateCatalogItemSidebar";
import UserTypesEnum from "../../../models/UserTypesEnum";
import IKit_Builder_Data from "../../../models/IKit_Builder_Data";
import { ProcessOrderEntryItemFn } from "../../../models/OrderEntryTypes";
import { KitBuilderModal } from "../KitBuilderModal/KitBuilderModal";
import INewOrder_Item_Option from "@models/INewOrder_Item_Option";
import { OrderItemOptions } from "@components/shared/OrderItemOptions/OrderItemOptions";
import { useForm } from "react-hook-form";
import { ReactHookInput } from "@components/shared/ReactHookInput/ReactHookInput";
import { IAddBulkItems } from "@models/IAddBulkItems";
import { ProductGroupSelector } from "@components/shared/ProductGroupSelector/ProductGroupSelector";
import { DimensionWidget } from "@components/shared/DimensionWidget/DimensionWidget";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";

type Props = {
    order: IOrder;
    addItemFn: ProcessOrderEntryItemFn;
    addItemBulkFn: (items: IAddBulkItems[]) => void;
    processStandardItemFn: (item: any, itemNo?: number) => INewOrder_Item;
    getNextItemNoFn: () => number;
    searchPhrase?: string;
    // Clear the scan code
    clearScanCodeFn: () => void;
};

interface IItemLookupForm {
    qty: number;
    itemOptions?: INewOrder_Item_Option[];
}

export const StandardCatalogItemLookup = (props: Props) => {
    const idbService: IdbService = new IdbService();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        watch,
    } = useForm<IItemLookupForm>({
        defaultValues: {
            qty: 1,
        },
    });

    // Watch the qty value to keep local state in sync
    const formQty = watch("qty");

    // Update local qty state when form qty changes
    useEffect(() => {
        setQty(formQty);
    }, [formQty]);

    const stickyScrollHandler = (e: any) => {
        if (e.target) {
            // @ts-expect-error
            const scrollTopY = e.target.scrollTop;

            const topHeaders = document.getElementsByTagName("header");

            let offsetTopY = 64;

            if (topHeaders.length) {
                if (topHeaders[0].offsetHeight > 0) {
                    offsetTopY = topHeaders[0].offsetHeight;
                } else {
                    if (topHeaders[1]) {
                        offsetTopY = topHeaders[1].offsetHeight;
                    }
                }
            }

            const itemLookup = innerLookupRef.current;
            const itemLookupContainer = standardItemLookupRef.current;
            const spacingDiv = itemLookupSpacingDiv.current;

            if (itemLookup && itemLookupContainer && spacingDiv) {
                const itemLookupBoundingRect = itemLookupContainer.getBoundingClientRect();

                if (itemLookupBoundingRect.y - itemLookupBoundingRect.height <= -itemLookupBoundingRect.height) {
                    spacingDiv.style.height = `${itemLookup.getBoundingClientRect().height}px`;
                    itemLookup.style.top = `${offsetTopY}px`;

                    setIsLookupSticky(true);
                } else {
                    spacingDiv.style.height = `0px`;

                    setIsLookupSticky(false);
                }
            }
        }
    };

    const params = useParams<{ order_guid: string }>();

    const standardItemLookupRef = useRef<HTMLDivElement | null>(null);
    const innerLookupRef = useRef<HTMLDivElement | null>(null);
    const itemLookupSpacingDiv = useRef<HTMLDivElement | null>(null);

    const getIsOfflineOrder = (): boolean => {
        return params.order_guid ? params.order_guid.indexOf("offline") >= 0 : false;
    };

    const [searchPhrase, setSearchPhrase] = useState(props.searchPhrase || "");
    const [typeaheadItems, setTypeaheadItems] = useState<ICatalog_Item[]>([]);
    const [typeaheadItem, setTypeaheadItem] = useState<ICatalog_Item | undefined>(undefined);
    const [selectedTypeahead, setSelectedTypeahead] = useState(false);
    const [qty, setQty] = useState(1);
    const [depth, setDepth] = useState(0);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const [enableAdd, setEnableAdd] = useState(false);
    const [autoSave, setAutoSave] = useState(true);
    const [showInventoryMessage, setShowInventoryMessage] = useState(true);
    const [warehouseInventoryAvailable, setWarehouseInventoryAvailable] = useState(true);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isOfflineOrder, setIsOfflineOrder] = useState(getIsOfflineOrder());
    const [isLookupSticky, setIsLookupSticky] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(uiContext.menuOpen);
    const [isMenuIconMode, setIsMenuIconMode] = useState(uiContext.menuIconMode);
    const [lookupWidth, setLookupWidth] = useState("100vw");
    const [lookupLeft, setLookupLeft] = useState("15px");
    const [kitOptions, setKitOptions] = useState<IKitDetails[]>([]);
    const [kitBuilderOptions, setKitBuilderOptions] = useState<IKit_Builder_Data>();
    const [warehouses, setWarehouses] = useState<IWarehouse[]>([]);
    const [filterProductGroupItems, setFilterProductGroupItems] = useState(true);
    const [retrievedWarehouses, setRetrievedWarehouses] = useState(!supplierModel.has_warehouses);
    const [retrievingWarehouses, setRetrievingWarehouses] = useState(!!supplierModel.has_warehouses);
    const [searchFilterSelections, setSearchFilterSelections] = useState({ name: true, sku: true, description: true, upc: true });
    const [catalogsOptions, setCatalogsOptions] = useState<
        { catalogGuid: string; catalogName: string; catalogId: number; hasCategories?: boolean }[]
    >([]);
    const [clearTypeahead, setClearTypeahead] = useState(false);
    const [inventoryMessage, setInventoryMessage] = useState<IInventoryMessage>({ title: "", className: "" });
    const [selectedItemDiscount, setSelectedItemDiscount] = useState<IDiscount | undefined>(undefined);
    const [selectedCatalogOptions, setSelectedCatalogOptions] = useState<
        { catalogGuid: string; catalogName: string; catalogId: number; hasCategories?: boolean } | undefined
    >(undefined);
    const [selectedUomSet, setSelectedUomSet] = useState<IUnit_Of_Measurement_Set | undefined>(undefined);
    const [selectedItemOptions, setSelectedItemOptions] = useState<Partial<INewOrder_Item_Option>[]>([]);

    const onGetWarehouses = (data: IOnGetWarehousesDTO) => {
        setWarehouses(data.warehouses);
        setRetrievedWarehouses(true);
        setRetrievingWarehouses(false);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        if (data.catalogsDetails) {
            const _selectedCatalogOptions = data.catalogsDetails.find((detail) => detail.catalogId === props.order.catalog_id);

            setCatalogsOptions(data.catalogsDetails);
            setSelectedCatalogOptions(_selectedCatalogOptions);
        }
    };

    const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        if (!_.isNil(data)) {
            setTypeaheadItems(data.catalog_items);
        }
    };

    const onCreateOrderEntryItems = () => {
        setClearTypeahead(true);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadCatalogItem },
        { endpoint: EndpointsEnum.OrderEntryOnCreateOrderEntryItems, fn: onCreateOrderEntryItems },
        { endpoint: EndpointsEnum.WarehouseOnGetWarehouses, fn: onGetWarehouses },
        { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
    ];

    useEffect(() => {
        const lookupSize = UIService.getLookupSize(uiContext.menuOpen, uiContext.menuIconMode, true, 0);

        setLookupLeft(lookupSize.lookupLeft);
        setLookupWidth(lookupSize.lookupWidth);
        setIsMenuOpen(uiContext.menuOpen);
    }, [uiContext.menuOpen, uiContext.menuIconMode]);

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        const mainView = document.getElementById("mainView");

        if (mainView) {
            mainView.onscroll = stickyScrollHandler;
        }

        window.addEventListener("online", setOnlineStatus);
        window.addEventListener("offline", setOnlineStatus);
        window.addEventListener("onresize", handleWindowResize);

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }

            window.removeEventListener("online", setOnlineStatus);
            window.removeEventListener("offline", setOnlineStatus);
            window.removeEventListener("onresize", handleWindowResize);

            if (mainView) {
                mainView.onscroll = null;
            }
        };
    }, []);

    const getLookupSize = (_isMenuOpen?: boolean, _isMenuIconMode?: boolean) => {
        let _lookupWidth = "100%";
        let _lookupLeft = "0px";

        if (_.isNil(isMenuOpen)) {
            _isMenuOpen = isMenuOpen;
        }

        if (_.isNil(isMenuIconMode)) {
            _isMenuIconMode = isMenuIconMode;
        }

        const mainView = document.getElementById("mainView");

        if (mainView && isMenuOpen) {
            const padding = 0;
            _lookupWidth = `${mainView.offsetWidth}px`;
            _lookupLeft = `${mainView.offsetLeft + padding}px`;
        }

        return { _lookupWidth, _lookupLeft };
    };

    const handleWindowResize = () => {
        const { _lookupWidth, _lookupLeft } = getLookupSize();

        setLookupWidth(_lookupWidth);
        setLookupLeft(_lookupLeft);
    };

    const setupSockets = () => {
        if (!isOfflineOrder) {
            for (const _socket of sockets) {
                socket.on(_socket.endpoint, _socket.fn);
            }
        }
    };

    const setOnlineStatus = () => {
        if (!navigator.onLine) {
            // do nothing?
        } else {
            setupSockets();
        }

        setIsOnline(navigator.onLine);
    };

    useEffect(() => {
        doTypeahead(searchPhrase);
    }, [searchPhrase]);

    const doTypeahead = async (typeahead: string) => {
        let vendorGuid;

        // if its NOT the suppliers house account, add the vendorGuid
        if (!_.isNil(props.order.supplier_id) && vendorModel) {
            vendorGuid = vendorModel.guid;
        }

        const order = props.order!;

        const data: ITypeaheadCatalogItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: order.Vendor!.guid,
            catalogGuid: order.Catalog!.guid,
            name: typeahead,
            // categoryId: props.order.category_id,
            orderGuid: props.order.guid,
            onlyActive: true,
            limit: 100,
            includeKitItems: true,
            ignoreProductGroups: !filterProductGroupItems,
            includeOptions: true,
            includeAttributes: true,
            options: {
                searchName: searchFilterSelections.name,
                searchSku: searchFilterSelections.sku,
                searchDescription: searchFilterSelections.description,
                searchUpc: searchFilterSelections.upc,
            },
        };

        if (typeahead.length) {
            if (isOnline && !isOfflineOrder) {
                socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, data);
            } else {
                const foundCatalogItems = await idbService.findCatalogItemsByString(order.Catalog!, typeahead);

                setTypeaheadItems(foundCatalogItems);
            }
        }
    };

    useEffect(() => {
        // console.log("searchFilterSelections: ", searchFilterSelections);
    }, [searchFilterSelections]);

    const toggleSearchFilter = (item: string) => {
        const _searchFilterSelections = { ...searchFilterSelections };

        // @ts-expect-error
        _searchFilterSelections[item] = !_searchFilterSelections[item];

        setSearchFilterSelections(_searchFilterSelections);
    };

    useEffect(() => {
        doInventoryValidation();
    }, [typeaheadItem, selectedUomSet, formQty]);

    const selectItem = (_typeaheadItem: ICatalog_Item) => {
        let _enableAdd = false;

        if (supplierModel.allow_neg_order_qty || (qty && qty > 0)) {
            _enableAdd = true;
        }

        if (_typeaheadItem) {
            if (!_.isNil(_typeaheadItem.min_order_qty)) {
                if (supplierModel.allow_neg_order_qty || (qty && qty >= _typeaheadItem.min_order_qty)) {
                    _enableAdd = true;
                } else {
                    _enableAdd = false;
                }
            }
        }

        const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(_typeaheadItem, supplierModel, props.order);

        if (supplierModel.has_uom_sets && _typeaheadItem.Base_Unit) {
            _enableAdd = false;
        }

        setEnableAdd(enableAdd);
        setTypeaheadItem(_typeaheadItem);
        setEnableAdd(_enableAdd);
        setSelectedItemDiscount(priceDetail.discount);
        setShowInventoryMessage(false);
        setInventoryMessage({ title: "", className: "" });
    };

    useEffect(() => {
        if (kitBuilderOptions && typeaheadItem) {
            addItemToOrder();
        }
    }, [kitBuilderOptions]);

    const resetTypeahead = () => {
        setEnableAdd(false);
        setTypeaheadItem(undefined);
        setValue("qty", 1); // Reset form qty
        setQty(1);
        setInventoryMessage({ title: "", className: "" });
        setShowInventoryMessage(false);
        setSelectedItemDiscount(undefined);
        setSelectedUomSet(undefined);
        setSelectedItemOptions([]);
        setClearTypeahead(true);
        setDepth(0);
        setWidth(0);
        setHeight(0);
    };

    const addItemBulkFn = (items: IAddBulkItems[]) => {
        props.addItemBulkFn(items);

        resetTypeahead();
    };

    const addItemToOrder = () => {
        if (typeaheadItem) {
            const _typeaheadItem: ICatalog_Item = { ...typeaheadItem };

            // Use the current form qty value
            const currentQty = getValues("qty");
            // @ts-expect-error - This is specifically for the payload when adding an item.
            _typeaheadItem.qty = CatalogItemService.getPackItemOrderQty(typeaheadItem, currentQty);

            let _kitOptions: IKitDetails[] | undefined = { ...kitOptions };

            if (!_typeaheadItem.is_kit) {
                _kitOptions = undefined;
            }

            if (depth) {
                _typeaheadItem.depth = depth;
            }

            if (width) {
                _typeaheadItem.width = width;
            }

            if (height) {
                _typeaheadItem.height = height;
            }

            props.addItemFn(_typeaheadItem, {
                shouldAutoSave: autoSave,
                kitDetails: _kitOptions,
                discount: selectedItemDiscount,
                uomSet: selectedUomSet,
                kitBuilderOptions,
                itemOptions: selectedItemOptions,
            });

            resetTypeahead();
        }
    };

    const isSelectableItem = (item: any) => {
        let isSelectable = true;

        const qtyTracked = !_.isNil(item.qty_on_hand);
        let isInStock;

        if (!_.isNil(qtyTracked)) {
            isInStock = _.get(item, "qty_on_hand", 0);
        }

        if (qtyTracked && !isInStock) {
            const allowBackorder = _.get(item, "allow_backorder", false);

            if (!allowBackorder) {
                isSelectable = false;
            }
        }

        return isSelectable;
    };

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setTypeaheadItem(undefined);
        setEnableAdd(false);
        setValue("qty", 1); // Reset form qty
        setQty(1);
        setInventoryMessage({ title: "", className: "" });
        setShowInventoryMessage(false);
    };

    const getSidebarComponent = () => {
        return (
            <CatalogViewerSidebar
                label={"catalog_viewer"}
                cancel={cancelSidebar}
                addToOrder={props.addItemBulkFn}
                order={props.order}
                isOfflineOrder={isOfflineOrder}
                isOnline={isOnline}
                warehouses={warehouses}
            />
        );
    };

    const getCategorySidebarComponent = () => {
        return (
            <OwCatalogCategoryViewer
                catalogGuid={props.order.Catalog!.guid}
                cancel={cancelSidebar}
                addToOrder={props.addItemBulkFn}
                order={props.order}
                warehouses={warehouses}
            />
        );
    };

    const getRecentItemsSidebarComponent = () => {
        return (
            <AccountRecentItemsSidebar
                catalogGuid={props.order.Catalog!.guid}
                cancel={cancelSidebar}
                addToOrder={props.addItemBulkFn}
                order={props.order}
                warehouses={warehouses}
            />
        );
    };

    const cancelSidebar = () => {
        uiContext.toggleSidebar();

        // Re-establish the found typeahead catalogGuid items
        socket.on(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);
    };

    const openCatalogViewer = () => {
        socket.removeListener(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);

        const sidebarComp = getSidebarComponent();
        uiContext.setSidebarComponent(sidebarComp);
        uiContext.toggleSidebar();
    };

    const openCategoryViewer = () => {
        socket.removeListener(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);

        const sidebarComp = getCategorySidebarComponent();
        uiContext.setSidebarComponent(sidebarComp);
        uiContext.toggleSidebar();
    };

    const openRecentItemsSidebar = () => {
        const sidebarComp = getRecentItemsSidebarComponent();
        uiContext.setSidebarComponent(sidebarComp);
        uiContext.toggleSidebar();
    };

    /**
     * Calculate the proper step value for the quantity input
     * Takes the smaller of unit_incr_qty or the supplier's qty precision step
     */
    const getQtyStepValue = () => {
        if (!typeaheadItem) return 1;

        let step = 1;

        // Get the step based on supplier's qty_precision
        if (supplierModel.qty_precision !== undefined && supplierModel.qty_precision > 0) {
            switch (supplierModel.qty_precision) {
                case 1:
                    step = 0.1;
                    break;
                case 2:
                    step = 0.01;
                    break;
                case 3:
                    step = 0.001;
                    break;
                default:
                    step = 1;
                    break;
            }
        }

        // If item has unit_incr_qty, use the smaller value to allow decimals
        if (typeaheadItem.unit_incr_qty && typeaheadItem.unit_incr_qty > 0) {
            // If supplier allows decimals, use the smaller step
            // This allows decimal input while buttons still increment by unit_incr_qty
            if (supplierModel.qty_precision && supplierModel.qty_precision > 0) {
                step = Math.min(step, typeaheadItem.unit_incr_qty);
            } else {
                // If no decimal precision, use unit_incr_qty as step
                step = typeaheadItem.unit_incr_qty;
            }
        }

        console.log("step", step);

        return step;
    };

    const incrementQty = () => {
        if (typeaheadItem?.is_product_group || typeaheadItem?.is_kit) {
            return;
        }

        const currentQty = getValues("qty");
        let initialQty = Number(currentQty) || 1;
        let _qty = initialQty + 1;

        if (typeaheadItem?.unit_incr_qty) {
            initialQty = Number(_qty) || 0;

            if (typeaheadItem?.unit_incr_qty > 1) {
                if (initialQty % typeaheadItem.unit_incr_qty > 0) {
                    initialQty = 0;
                }

                _qty = initialQty + Number(typeaheadItem?.unit_incr_qty);
            }
        }

        if (typeaheadItem?.qty_on_hand) {
            if (_qty > Number(typeaheadItem?.qty_on_hand) && !typeaheadItem.allow_backorder) {
                _qty = Number(typeaheadItem.qty_on_hand);
            }
        }

        setValue("qty", _qty);
    };

    const decrementQty = () => {
        if (typeaheadItem?.is_product_group || typeaheadItem?.is_kit) {
            return;
        }

        const currentQty = getValues("qty");
        let initialQty = Number(currentQty) || 1;
        let _qty = initialQty - 1;

        if (typeaheadItem?.unit_incr_qty) {
            initialQty = Number(currentQty) || 0;

            if (typeaheadItem?.unit_incr_qty > 1) {
                _qty = initialQty - Number(typeaheadItem?.unit_incr_qty);
            }
        }

        if (_qty >= 1 || supplierModel.allow_neg_order_qty) {
            setValue("qty", _qty);
        }
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        // qty is now handled by react-hook-form, so handleChange is mainly for other form elements
        // Add any future non-qty form handling here if needed
    };

    /**
     * Validates if an item can be added
     */
    const doInventoryValidation = () => {
        // Check if the item has inventory flag turned on

        // if on, then check the current qty entered. Only allow up to a maximum of qty on hand, unless backorder. If backorder, tell user qty that will be on backorder
        if (typeaheadItem) {
            const currentQty = getValues("qty");

            if (typeaheadItem.has_inventory) {
                const inventoryValidationDetail = OrderEntryService.doInventoryValidation(
                    typeaheadItem,
                    currentQty || 0,
                    props.order.Order_Items,
                    supplierModel,
                    props.order,
                    0,
                    selectedUomSet
                );

                // console.log(enableAdd, qty, showInventoryMessage, inventoryMessage, warehouseInventoryAvailable);
                if (!showInventoryMessage) {
                    inventoryValidationDetail.showInventoryMessage = false;
                }

                if (supplierModel.has_uom_sets && typeaheadItem.Base_Unit) {
                    if (!selectedUomSet) {
                        inventoryValidationDetail.enableAdd = false;
                    }
                }

                setEnableAdd(inventoryValidationDetail.enableAdd);
                if (inventoryValidationDetail.qty !== currentQty) {
                    setValue("qty", inventoryValidationDetail.qty);
                }
                setShowInventoryMessage(inventoryValidationDetail.showInventoryMessage);
                setInventoryMessage(inventoryValidationDetail.inventoryMessage);
                setWarehouseInventoryAvailable(inventoryValidationDetail.warehouseInventoryAvailable);
            } else {
                const inventoryValidationDetail = OrderEntryService.doInventoryValidation(
                    typeaheadItem,
                    currentQty || 0,
                    props.order.Order_Items,
                    supplierModel,
                    props.order,
                    0
                );

                setEnableAdd(inventoryValidationDetail.enableAdd);
            }
        }
    };

    const addProductGroupItems = (items: IAddBulkItems[]) => {
        if (!supplierModel || !userModel) {
            return;
        }

        let nextItemNo = props.getNextItemNoFn();

        const processedItems = items.map((details) => {
            const processedItem = props.processStandardItemFn(details.item, nextItemNo);
            nextItemNo++;
            return {
                item: processedItem,
                kitDetails: details.kitDetails,
                kitBuilderData: details.kitBuilderData,
                itemOptionValues: details.itemOptionValues,
            };
        });

        const payload: ICreateOrderEntryItemsDTO = {
            supplierGuid: supplierModel.guid,
            userToken: userModel.token,
            userGuid: userModel.guid,
            orderGuid: props.order.guid,
            items: processedItems,
        };

        if (!userModel.isSupplierUser || props.order.supplier_id !== supplierModel.id) {
            if (vendorModel) {
                payload.vendorGuid = vendorModel.guid;
            }
        }

        socket.emit(EndpointsEnum.OrderEntryCreateOrderEntryItems, payload);
    };

    const showCreateItemModal = () => {
        const sideBarComponent = (
            <CreateCatalogItemSidebar
                catalogModel={props.order.Catalog!}
                supplierModel={supplierModel}
                notifyFn={uiContext.notifyFn}
                cancel={closeCreateItemModal}
                socket={socket}
                catalogGuid={props.order.Catalog!.guid}
                userModel={userModel}
                priceGroups={[props.order.Price_Group!]}
                vendorSources={[]}
            />
        );

        uiContext.setSidebarComponent(sideBarComponent);
        uiContext.toggleSidebar(true);
    };

    const closeCreateItemModal = (refreshData?: boolean, newItem?: ICatalog_Item) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (newItem) {
            setTypeaheadItem(newItem);
        }
    };

    const renderLookupItems = (item: ICatalog_Item) => {
        return (
            <div>
                <div>
                    <span dangerouslySetInnerHTML={{ __html: item.name }} />
                    {item.is_kit ? <OwLabel className={"ms-1"} title={"kit"} preText={"("} postText={")"} /> : null}
                </div>
                <div className={"text-small text-muted"}>
                    {item.short_description?.length ? (
                        item.short_description
                    ) : (
                        <CatalogItemDescription catalogItem={item} priceGroup={props.order.Price_Group} />
                    )}
                </div>
            </div>
        );
    };

    const updateKitOptionSelection = (options: { id: number; qty: number }[]) => {
        let _enableAdd = false;

        for (const option of options) {
            if (option.qty > 0) {
                _enableAdd = true;
            }
        }

        setEnableAdd(_enableAdd);
        setKitOptions(options);
    };

    const getQtyOnHand = () => {
        let qtyOnHand = 0;

        if (typeaheadItem) {
            qtyOnHand = typeaheadItem.qty_on_hand || 0;

            if (typeaheadItem.Material_Catalog_Item_Maps?.length) {
                qtyOnHand = CatalogItemService.getItemQtyOnHandBasedOnMaterials(typeaheadItem);
            }

            if (supplierModel.has_warehouses && typeaheadItem.Warehouse_Catalog_Items_Maps) {
                let availableInventory = 0;

                for (const mapItem of typeaheadItem.Warehouse_Catalog_Items_Maps) {
                    if (mapItem.warehouse_id === props.order.shipping_warehouse_id) {
                        availableInventory = mapItem.qty_on_hand;
                    }
                }

                qtyOnHand = availableInventory;
            }
        }

        return qtyOnHand;
    };

    /**
     * Sets the discount for the catalog item that is selected
     * automatically by the UI.
     *
     * @param discount
     */
    const setDiscount = (discount: IDiscount) => {
        setSelectedItemDiscount(discount);
    };

    const selectedUomSetFn = (set: IUnit_Of_Measurement_Set) => {
        setSelectedUomSet(set);
    };

    const selectedItemOptionsFn = (options: any) => {
        setSelectedItemOptions(options);
    };

    const updateTypeaheadValue = (str: string) => {
        setSearchPhrase(str);
    };

    const onSubmit = (data: IItemLookupForm) => {
        // Update the local qty state to match the form data
        setQty(data.qty);
        // Trigger add to order with the form data
        if (typeaheadItem && enableAdd) {
            addItemToOrder();
        }
    };

    const getOrderLookup = () => {
        let detailSideFlexDir = "flex-row";
        let detailsMargin = "ms-3";

        if (typeaheadItem) {
            const { group_items } = typeaheadItem;

            if (group_items && group_items.length > 6) {
                detailSideFlexDir = "flex-column";
                detailsMargin = "pt-3";
            }
        }

        const uomSetQtyConversion = OrderEntryService.getUomSetQty(qty || 1, typeaheadItem?.Base_Unit, selectedUomSet);

        const showCreateNew =
            !!supplierModel.allow_create_item_in_order_entry &&
            userModel.isSupplierUser &&
            userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN;

        return (
            <div
                ref={innerLookupRef}
                className={"flex-column entry-row " + (isLookupSticky ? " sticky ms-0 " : " ")}
                style={{ width: lookupWidth, left: lookupLeft }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={"row w-100 align-self-start"}>
                        <div className={"col-12 col-md-6 col-lg-4 col-xl-3 me-2"}>
                            <div className="position-relative" id={"itemLookup"}>
                                <div>
                                    <Typeahead
                                        typeaheadItems={typeaheadItems}
                                        clearTypeaheadCallback={clearTypeaheadFn}
                                        clearTypeahead={clearTypeahead}
                                        titleProp="name"
                                        subTitleProp="description"
                                        maxResults={isOnline ? 100 : 100}
                                        placeholder="Search Catalog Items"
                                        selectedItem={typeaheadItem}
                                        isSelectableFn={isSelectableItem}
                                        selectedItemFn={selectItem}
                                        typeaheadApiFn={updateTypeaheadValue}
                                        scrollableList={true}
                                        defaultClassName={"form-control rounded-end-2"}
                                        searchPhrase={searchPhrase}
                                        itemsRenderFn={renderLookupItems}
                                        createNewFn={showCreateNew ? showCreateItemModal : undefined}
                                        showCreateNew={showCreateNew}
                                        createNewText={showCreateNew ? "create_new_item" : undefined}
                                        stickyCreateNewToBottom={showCreateNew}
                                        sideFilter={true}
                                        sideFilterItems={[
                                            {
                                                name: "name",
                                                children: (
                                                    <OwCheckbox
                                                        label={"name"}
                                                        name={"name"}
                                                        checked={searchFilterSelections.name}
                                                        fn={() => toggleSearchFilter("name")}
                                                    />
                                                ),
                                            },
                                            {
                                                name: "sku",
                                                children: (
                                                    <OwCheckbox
                                                        label={"sku"}
                                                        name={"sku"}
                                                        checked={searchFilterSelections.sku}
                                                        fn={() => toggleSearchFilter("sku")}
                                                    />
                                                ),
                                            },
                                            {
                                                name: "description",
                                                children: (
                                                    <OwCheckbox
                                                        label={"description"}
                                                        checked={searchFilterSelections.description}
                                                        name={"description"}
                                                        fn={() => toggleSearchFilter("description")}
                                                    />
                                                ),
                                            },
                                        ]}
                                    />
                                    {supplierModel.show_filter_product_groups_in_order_entry ? (
                                        <div>
                                            <OwCheckbox
                                                label={"filter_product_group_items"}
                                                name={"filterProductGroupItems"}
                                                fn={handleChange}
                                                checked={filterProductGroupItems}
                                                truthyVal={true}
                                                falsyVal={false}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <div className="col-auto">
                            <ItemLookupButtonSet
                                openCategoryViewer={openCategoryViewer}
                                openCatalogViewer={openCatalogViewer}
                                retrievedWarehouses={retrievedWarehouses}
                                retrievingWarehouses={retrievingWarehouses}
                                selectedCatalogOptions={selectedCatalogOptions}
                                openRecentItemsSidebar={openRecentItemsSidebar}
                            />
                        </div>
                    </div>
                    {typeaheadItem && (
                        <div className={"col"}>
                            <hr />
                        </div>
                    )}
                    {typeaheadItem && (
                        <div className={"row align-self-start align-items-end"}>
                            <div className="col-auto">
                                <div>
                                    {isOnline && (
                                        <div>
                                            <div>
                                                <OwS3ImageViewer
                                                    catalogItem={typeaheadItem}
                                                    isMicroView={true}
                                                    attachmentIndex={0}
                                                    imageIndex={IImageSizeEnum.THUMB}
                                                    className={"me-2"}
                                                    includeAllImages={true}
                                                    hideEmptyImage={true}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={"col-auto"}>
                                <div>
                                    <b>{typeaheadItem ? typeaheadItem.name : null}</b>
                                </div>
                                <div>
                                    <OwLabel title={"uom"} className={"me-2"} isBold={true} noBottomMargin={true} postText={":"} />
                                    {typeaheadItem && typeaheadItem.Unit_Of_Measurement ? typeaheadItem.Unit_Of_Measurement.name : null}
                                </div>
                            </div>
                            {typeaheadItem.is_product_group ? null : (
                                <>
                                    <div className="col-auto" id={"itemQty"}>
                                        <div className={"qty-change no-wrap mb-2 mb-md-0"}>
                                            <div className={"input-group no-wrap"}>
                                                <OwButton
                                                    centerIconClass={"fas fa-minus"}
                                                    fn={decrementQty}
                                                    btnSizeClass={" "}
                                                    disabled={typeaheadItem?.is_product_group || typeaheadItem?.is_kit}
                                                    testId={"decrement-qty-btn"}
                                                    disableDblClickPrevention={true}
                                                />
                                                <ReactHookInput
                                                    register={register("qty", { required: true })}
                                                    name={"qty"}
                                                    className={"text-end"}
                                                    type={"number"}
                                                    disablePrecisionCheck={true}
                                                    disabled={typeaheadItem && (typeaheadItem.is_product_group || typeaheadItem.is_kit)}
                                                    defaultClassName={"form-control"}
                                                    step={getQtyStepValue()}
                                                    onWheel={UIService.stopScrollWheel}
                                                />
                                                <OwButton
                                                    centerIconClass={"fas fa-plus"}
                                                    fn={incrementQty}
                                                    btnSizeClass={" "}
                                                    disabled={typeaheadItem?.is_product_group || typeaheadItem?.is_kit}
                                                    testId={"increment-qty-btn"}
                                                    disableDblClickPrevention={true}
                                                />
                                            </div>
                                        </div>

                                        {typeaheadItem?.min_order_qty ? (
                                            <div className={"text-small text-danger text-center"}>
                                                <OwLabel title={"min_order_qty"} postText={":"} /> {typeaheadItem.min_order_qty}
                                            </div>
                                        ) : null}
                                        {typeaheadItem ? <PackQtyViewer catalogItem={typeaheadItem} qty={qty} /> : null}
                                    </div>

                                    {typeaheadItem?.user_entered_depth && (
                                        <div className="col-auto">
                                            {supplierModel.show_dimension_widget ? (
                                                <DimensionWidget
                                                    type="depth"
                                                    fn={(value: number) => setDepth(value)}
                                                    value={depth}
                                                    units="imperial"
                                                    targetUnitId={typeaheadItem?.dimension_unit_id}
                                                />
                                            ) : (
                                                <OwFnInput fn={(value: number) => setDepth(value)} value={depth} name="depth" />
                                            )}
                                        </div>
                                    )}

                                    {supplierModel.has_uom_sets && typeaheadItem?.Base_Unit ? (
                                        <div className={"col-auto"}>
                                            <UomSetSelectOption item={typeaheadItem} selectedOption={selectedUomSetFn} />
                                        </div>
                                    ) : null}

                                    {typeaheadItem && typeaheadItem.Options?.length ? (
                                        <div className={"col-auto"}>
                                            <OrderItemOptions catalogItem={typeaheadItem} selectedItemOptionsFn={selectedItemOptionsFn} />
                                        </div>
                                    ) : null}

                                    <div className={"col-auto"}>
                                        <OwButton
                                            type="submit"
                                            id={"itemAdd"}
                                            disabled={!enableAdd || (typeaheadItem && typeaheadItem.is_product_group) || !!errors.qty}
                                            title={"add"}
                                            leftIconClass={"fas fa-plus"}
                                            btnSizeClass={" "}
                                        />
                                    </div>

                                    <div className="col-auto">
                                        {uomSetQtyConversion ? (
                                            <div>
                                                <i className={"fas fa-circle-info fa-xs"} /> {uomSetQtyConversion}{" "}
                                                {typeaheadItem?.Base_Unit?.unit_abbr}
                                            </div>
                                        ) : null}
                                    </div>
                                </>
                            )}

                            {typeaheadItem.is_product_group && (
                                <div className={"col-auto w-50r"}>
                                    <ProductGroupSelector
                                        supplierModel={supplierModel}
                                        catalogItem={typeaheadItem}
                                        order={props.order}
                                        notifyFn={uiContext.notifyFn}
                                        socket={socket}
                                        warehouses={warehouses}
                                        addItems={addItemBulkFn}
                                    />
                                </div>
                            )}

                            <div className={inventoryMessage && showInventoryMessage ? "col-12 " + inventoryMessage.className : "d-none"}>
                                <span className={inventoryMessage && inventoryMessage.title === "inventory" ? "" : "d-none"}>
                                    {inventoryMessage ? <OwLabel title={"only"} className={"me-1"} /> : null}
                                    {typeaheadItem ? getQtyOnHand() : ""} <OwLabel className={"mb-0"} title={"available"} />
                                    {supplierModel.has_warehouses ? (
                                        warehouseInventoryAvailable ? (
                                            <OwLabel className={"ms-1"} title={"in_warehouse"} />
                                        ) : (
                                            <OwLabel className={"ms-1"} title={"in_warehouse"} />
                                        )
                                    ) : null}
                                </span>
                            </div>
                        </div>
                    )}

                    {typeaheadItem && typeaheadItem.is_kit ? (
                        <div className={"col-12"}>
                            <hr />
                        </div>
                    ) : null}
                    <div className={"row"}>
                        <div className={"col-12 g-0s d-flex " + detailSideFlexDir}>
                            {typeaheadItem && typeaheadItem.is_kit && !typeaheadItem.is_kit_builder ? (
                                <div>
                                    <KitOptionSelector
                                        kitCatalogItem={typeaheadItem}
                                        order={props.order}
                                        userModel={userModel}
                                        supplierModel={supplierModel}
                                        vendorModel={vendorModel}
                                        updateItemSelections={updateKitOptionSelection}
                                        useOptionSelectionModal={true}
                                    />
                                </div>
                            ) : null}
                            {typeaheadItem && typeaheadItem.is_kit && typeaheadItem.is_kit_builder ? (
                                <div>
                                    <KitBuilderModal
                                        catalogItem={typeaheadItem}
                                        addToCartOrOrder={(selection: IKit_Builder_Data) => {
                                            setKitBuilderOptions(selection);
                                        }}
                                        disableSelection={false}
                                        saveEndpointReturn={EndpointsEnum.OrderEntryOnCreateOrderEntryItem}
                                    />
                                </div>
                            ) : null}
                        </div>
                        {typeaheadItem ? (
                            <div className={"col-12"}>
                                <hr />
                            </div>
                        ) : null}
                        <div className={"row"}>
                            <div className={"col-12 g-0s d-flex " + detailSideFlexDir}>
                                {typeaheadItem && (
                                    <CatalogItemLookupDetails
                                        isOnline={isOnline}
                                        order={props.order}
                                        typeaheadItem={typeaheadItem}
                                        qtyWanted={qty}
                                        setDiscount={setDiscount}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        );
    };

    const getGoOnlineDetail = () => {
        return (
            <div className={"row"}>
                <div className={"col-12"}>
                    <OwTitle
                        title={"go_online_to_add_items"}
                        tag={"h5"}
                        className={"mb-0 text-warning"}
                        leftIconName={"fad fa-exclamation-triangle"}
                    />
                </div>
            </div>
        );
    };

    const getCatalogEntryRowView = () => {
        if (isOfflineOrder) {
            return getOrderLookup();
        } else {
            if (isOnline) {
                return getOrderLookup();
            } else {
                return getGoOnlineDetail();
            }
        }
    };

    return (
        <div
            ref={standardItemLookupRef}
            id={"catalogItemLookup"}
            className={"StandardCatalogItemLookup ps-3 pe-3 pt-0 " + (isOfflineOrder || isOnline ? "pb-3" : "")}>
            <div ref={itemLookupSpacingDiv}></div>
            {getCatalogEntryRowView()}

            <ReactTooltip
                getContent={(dataTip) => {
                    return dataTip;
                }}
                effect="solid"
                place="right"
            />
        </div>
    );
};
